import { useMemo, useState } from "react";
import type {
  DocumentAttachmentKind,
  DocumentScript,
  PublicDocumentAttachment,
} from "@vedaunion/shared";
import {
  useDocumentAttachments,
  useCreateAttachment,
  useUpdateAttachment,
  useDeleteAttachment,
  useReorderAttachments,
} from "../../lib/documents";
import { useUploadMedia } from "../../lib/media";

/**
 * Admin component for managing a document's attachments.
 *
 * Each attachment is one of:
 *   pdf — uploaded PDF, tagged with a script (devanāgarī, IAST, …)
 *   audio — uploaded audio (MP3 / OGG / WAV)
 *   youtube_video — references an existing channel video by id
 *   youtube_playlist — references a channel playlist by id
 *   external_link — a URL pointing elsewhere (a study page, an offsite PDF)
 *
 * Any of them can sit in a section or a folder (`groupKey`, read as a path by
 * `shared/src/folders.ts`): "Namakam", or "Namakam/Anuvāka 1".
 *
 * The "video" file upload kind is intentionally hidden from the picker —
 * raw video files don't go through our /api/media endpoint (videos live on
 * YouTube). The schema keeps the kind for future extensibility.
 */

const SCRIPT_OPTIONS: ReadonlyArray<{ value: DocumentScript; label: string }> = [
  { value: "devanagari", label: "Devanāgarī" },
  { value: "iast", label: "IAST" },
  { value: "tamil", label: "Tamil" },
  { value: "telugu", label: "Telugu" },
  { value: "kannada", label: "Kannada" },
  { value: "malayalam", label: "Malayalam" },
  { value: "bengali", label: "Bengali" },
  { value: "gurmukhi", label: "Gurmukhi" },
  { value: "gujarati", label: "Gujarati" },
  { value: "oriya", label: "Oriya" },
  { value: "sinhala", label: "Sinhala" },
  { value: "grantha", label: "Grantha" },
  { value: "sharada", label: "Śāradā" },
  { value: "tibetan", label: "Tibetan" },
  { value: "latin", label: "Latin" },
  { value: "other", label: "Other" },
];

type AddKind = "pdf" | "audio" | "youtube_video" | "youtube_playlist" | "external_link";

/** Accept a pasted YouTube URL (any form) OR a bare ID, and return just the ID.
 *  Video: watch?v=, youtu.be/, /shorts/, /embed/. Playlist: the list= param. */
function ytId(raw: string, kind: "youtube_video" | "youtube_playlist"): string {
  const s = raw.trim();
  if (!s) return "";
  if (kind === "youtube_playlist") {
    const m = s.match(/[?&]list=([A-Za-z0-9_-]+)/);
    return m?.[1] ?? s;
  }
  const m = s.match(/(?:youtu\.be\/|\/shorts\/|\/embed\/|[?&]v=)([A-Za-z0-9_-]{11})/);
  return m?.[1] ?? s;
}

export function AttachmentManager({ documentId }: { documentId: number }) {
  const attachmentsQ = useDocumentAttachments(documentId);
  const attachments = attachmentsQ.data?.attachments ?? [];
  const [adding, setAdding] = useState<AddKind | null>(null);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-grotesk text-[0.78rem] tracking-[0.04em] text-[var(--color-ink-soft)] mr-2">
          Add resource:
        </p>
        <AddButton onClick={() => setAdding("pdf")} active={adding === "pdf"}>
          PDF
        </AddButton>
        <AddButton onClick={() => setAdding("audio")} active={adding === "audio"}>
          Audio
        </AddButton>
        <AddButton onClick={() => setAdding("youtube_video")} active={adding === "youtube_video"}>
          YouTube video
        </AddButton>
        <AddButton onClick={() => setAdding("youtube_playlist")} active={adding === "youtube_playlist"}>
          YouTube playlist
        </AddButton>
        <AddButton onClick={() => setAdding("external_link")} active={adding === "external_link"}>
          External link
        </AddButton>
      </div>

      {adding && (
        <AddAttachmentForm
          documentId={documentId}
          kind={adding}
          onClose={() => setAdding(null)}
        />
      )}

      <AttachmentList documentId={documentId} attachments={attachments} />

      {/* Every folder this document already uses, offered as you type — so
          "Namakam/Anuvāka 1" is picked, not retyped (and mistyped) per file. */}
      <datalist id={FOLDER_LIST_ID}>
        {[...new Set(attachments.map((a) => a.groupKey).filter((g): g is string => !!g))]
          .sort((a, b) => a.localeCompare(b))
          .map((g) => (
            <option key={g} value={g} />
          ))}
      </datalist>
    </div>
  );
}

const FOLDER_LIST_ID = "attachment-folders";

/**
 * The ONE folder field, in the add form and in a row's edit. Any kind of
 * material can live in a folder — the public page groups by this path whatever
 * the file is (`shared/src/folders.ts`).
 */
function FolderField({
  value,
  onChange,
  withHint = false,
}: {
  value: string;
  onChange: (v: string) => void;
  withHint?: boolean;
}) {
  return (
    <div className="auth-field">
      <label className="auth-label">Section / folder (optional)</label>
      <input
        className="auth-input"
        type="text"
        list={FOLDER_LIST_ID}
        placeholder="e.g. Namakam  or  Namakam/Anuvāka 1"
        value={value}
        maxLength={64}
        onChange={(e) => onChange(e.target.value)}
      />
      {withHint && (
        <p className="auth-hint">
          A name alone ("Namakam") is a section of the materials. Add "/" and a
          folder name ("Namakam/Anuvāka 1") to put it in a folder of that
          section — the folder gets its own page and its own shareable link.
        </p>
      )}
    </div>
  );
}

function AddButton({
  onClick,
  active,
  children,
}: {
  onClick: () => void;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`chip ${active ? "is-active" : ""}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// -----------------------------------------------------------------------------
// Add form
// -----------------------------------------------------------------------------

function AddAttachmentForm({
  documentId,
  kind,
  onClose,
}: {
  documentId: number;
  kind: AddKind;
  onClose: () => void;
}) {
  const create = useCreateAttachment(documentId);
  const upload = useUploadMedia();
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [script, setScript] = useState<DocumentScript>("devanagari");
  const [groupKey, setGroupKey] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [youtubeId, setYoutubeId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const reset = () => {
    setLabel("");
    setDescription("");
    setScript("devanagari");
    setGroupKey("");
    setExternalUrl("");
    setYoutubeId("");
    setError(null);
  };

  const onPickFile = async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      const m = await upload.mutateAsync({ file });
      // Default the label to the original filename if blank.
      const finalLabel =
        label.trim() ||
        (m.originalFilename ?? "").replace(/\.[^.]+$/, "") ||
        (kind === "pdf" ? "PDF" : "Audio");

      await create.mutateAsync({
        kind,
        label: finalLabel,
        description: description.trim() || null,
        script: kind === "pdf" ? script : null,
        fileUrl: m.url,
        // Posters are rendered offline (tools/pdf-thumbs.py) and attached by the
        // seed; this form never invents one.
        thumbnailUrl: null,
        externalUrl: null,
        youtubeVideoId: null,
        youtubePlaylistId: null,
        durationSec: m.durationSec ?? null,
        bytes: m.bytes,
        mime: m.mime,
        groupKey: groupKey.trim() || null,
        sortOrder: 0,
      });
      reset();
      onClose();
    } catch (err) {
      const e = err as { body?: { message?: string }; message?: string };
      setError(e?.body?.message ?? e?.message ?? "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const onSaveLink = async (linkKind: "youtube_video" | "youtube_playlist" | "external_link") => {
    setError(null);
    if (!label.trim()) {
      setError("Label is required.");
      return;
    }
    try {
      await create.mutateAsync({
        kind: linkKind,
        label: label.trim(),
        description: description.trim() || null,
        script: null,
        fileUrl: null,
        thumbnailUrl: null,
        externalUrl: linkKind === "external_link" ? externalUrl.trim() : null,
        youtubeVideoId: linkKind === "youtube_video" ? youtubeId.trim() : null,
        youtubePlaylistId: linkKind === "youtube_playlist" ? youtubeId.trim() : null,
        durationSec: null,
        bytes: null,
        mime: null,
        groupKey: groupKey.trim() || null,
        sortOrder: 0,
      });
      reset();
      onClose();
    } catch (err) {
      const e = err as { body?: { message?: string }; message?: string };
      setError(e?.body?.message ?? e?.message ?? "Save failed.");
    }
  };

  return (
    <div className="veda-panel">
      <div className="flex items-baseline justify-between mb-3">
        <p className="page-eyebrow">Add {kindLabel(kind)}</p>
        <button type="button" className="row-action" onClick={onClose}>
          Cancel
        </button>
      </div>

      <div className="flex flex-col gap-3 max-w-xl">
        <div className="auth-field">
          <label className="auth-label">Label</label>
          <input
            className="auth-input"
            type="text"
            placeholder={labelPlaceholder(kind)}
            value={label}
            maxLength={250}
            onChange={(e) => setLabel(e.target.value)}
          />
          <p className="auth-hint">{labelHint(kind)}</p>
        </div>

        <div className="auth-field">
          <label className="auth-label">Description (optional)</label>
          <textarea
            className="auth-input"
            rows={2}
            value={description}
            maxLength={1000}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {kind === "pdf" && (
          <div className="auth-field">
            <label className="auth-label">Script</label>
            <select
              className="auth-input"
              value={script}
              onChange={(e) => setScript(e.target.value as DocumentScript)}
            >
              {SCRIPT_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <p className="auth-hint">
              Surfaced as a script chip on the public page so readers can jump
              straight to their preferred script.
            </p>
          </div>
        )}

        <FolderField value={groupKey} onChange={setGroupKey} withHint />

        {(kind === "pdf" || kind === "audio") && (
          <div className="auth-field">
            <label className="auth-label">File</label>
            <input
              type="file"
              accept={kind === "pdf" ? "application/pdf" : "audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/webm"}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void onPickFile(file);
              }}
              disabled={uploading || create.isPending}
            />
            <p className="auth-hint">
              {kind === "pdf"
                ? "Uploads up to 25 MB. Naming the PDF with its script helps later."
                : "Uploads up to 25 MB. MP3 keeps file size lean."}
            </p>
          </div>
        )}

        {kind === "youtube_video" && (
          <div className="auth-field">
            <label className="auth-label">YouTube video — URL or ID</label>
            <input
              className="auth-input"
              type="text"
              placeholder="https://youtu.be/dQw4w9WgXcQ  ·  or  dQw4w9WgXcQ"
              value={youtubeId}
              onChange={(e) => setYoutubeId(ytId(e.target.value, "youtube_video"))}
            />
            <p className="auth-hint">
              Paste the video link (any form) or the 11-character ID — we pull out
              the ID automatically. Title and thumbnail come from the channel mirror.
            </p>
          </div>
        )}

        {kind === "youtube_playlist" && (
          <div className="auth-field">
            <label className="auth-label">YouTube playlist — URL or ID</label>
            <input
              className="auth-input"
              type="text"
              placeholder="https://youtube.com/playlist?list=PL…  ·  or  PL…"
              value={youtubeId}
              onChange={(e) => setYoutubeId(ytId(e.target.value, "youtube_playlist"))}
            />
            <p className="auth-hint">
              Paste the playlist link (the <code>list=…</code> URL) or the ID —
              we pull out the ID. Title and item count come from the channel mirror.
            </p>
          </div>
        )}

        {kind === "external_link" && (
          <div className="auth-field">
            <label className="auth-label">URL</label>
            <input
              className="auth-input"
              type="url"
              placeholder="https://"
              value={externalUrl}
              maxLength={500}
              onChange={(e) => setExternalUrl(e.target.value)}
            />
            <p className="auth-hint">
              Where the link should take the reader. Useful for offsite PDFs,
              archive pages, related study guides on other sites.
            </p>
          </div>
        )}

        {error && <p className="auth-error">— {error}</p>}

        {(kind === "youtube_video" || kind === "youtube_playlist" || kind === "external_link") && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="btn-app"
              onClick={() => void onSaveLink(kind)}
              disabled={create.isPending}
            >
              {create.isPending ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn-app-ghost" onClick={onClose}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function kindLabel(k: AddKind): string {
  switch (k) {
    case "pdf":
      return "a PDF";
    case "audio":
      return "an audio recording";
    case "youtube_video":
      return "a YouTube video";
    case "youtube_playlist":
      return "a YouTube playlist";
    case "external_link":
      return "an external link";
  }
}

function labelPlaceholder(k: AddKind): string {
  switch (k) {
    case "pdf":
      return "Devanāgarī script";
    case "audio":
      return "Anuvāka 1";
    case "youtube_video":
      return "Class 3 — Sri Rudram, anuvāka 4";
    case "youtube_playlist":
      return "Sri Rudram class series";
    case "external_link":
      return "Sanskrit Documents — Sri Rudram";
  }
}

function labelHint(k: AddKind): string {
  switch (k) {
    case "pdf":
      return "Shown to the reader. \"Devanāgarī script\", \"IAST script\", or your own wording.";
    case "audio":
      return "Shown to the reader. For series, name the segment (Anuvāka 1, 2, …).";
    case "youtube_video":
      return "Shown alongside the embedded video.";
    case "youtube_playlist":
      return "Shown alongside the playlist link.";
    case "external_link":
      return "Shown as the link text. Make it descriptive — readers see this, not the URL.";
  }
}

// -----------------------------------------------------------------------------
// List + per-row controls
// -----------------------------------------------------------------------------

function AttachmentList({
  documentId,
  attachments,
}: {
  documentId: number;
  attachments: PublicDocumentAttachment[];
}) {
  const reorder = useReorderAttachments(documentId);

  const onMove = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= attachments.length) return;
    const ids = attachments.map((a) => a.id);
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    void reorder.mutateAsync(ids);
  };

  if (attachments.length === 0) {
    return (
      <p className="font-grotesk text-[0.85rem] text-[var(--color-ink-mute)] italic">
        No resources yet — add a PDF, audio recording, or link above.
      </p>
    );
  }

  return (
    <ul className="flex flex-col">
      {attachments.map((a, i) => (
        <AttachmentRow
          key={a.id}
          documentId={documentId}
          attachment={a}
          isFirst={i === 0}
          isLast={i === attachments.length - 1}
          onMoveUp={() => onMove(i, -1)}
          onMoveDown={() => onMove(i, 1)}
        />
      ))}
    </ul>
  );
}

function AttachmentRow({
  documentId,
  attachment,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
}: {
  documentId: number;
  attachment: PublicDocumentAttachment;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const update = useUpdateAttachment(documentId);
  const remove = useDeleteAttachment(documentId);
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(attachment.label);
  const [description, setDescription] = useState(attachment.description ?? "");
  const [script, setScript] = useState<DocumentScript>(
    (attachment.script as DocumentScript | null) ?? "devanagari",
  );
  const [groupKey, setGroupKey] = useState(attachment.groupKey ?? "");

  const summary = useMemo(() => attachmentSummary(attachment), [attachment]);

  const onSave = async () => {
    await update.mutateAsync({
      id: attachment.id,
      body: {
        label: label.trim(),
        description: description.trim() || null,
        ...(attachment.kind === "pdf" ? { script } : {}),
        groupKey: groupKey.trim() || null,
      },
    });
    setEditing(false);
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete "${attachment.label}"? This cannot be undone.`)) return;
    await remove.mutateAsync(attachment.id);
  };

  return (
    <li className="grid grid-cols-1 sm:grid-cols-[28px_1fr_auto] gap-3 sm:gap-4 py-3 border-t border-[var(--color-rule)] first:border-t-0 items-baseline">
      <div className="flex sm:flex-col gap-1 items-center">
        <button
          type="button"
          className="row-action"
          onClick={onMoveUp}
          disabled={isFirst}
          aria-label="Move up"
        >
          ↑
        </button>
        <button
          type="button"
          className="row-action"
          onClick={onMoveDown}
          disabled={isLast}
          aria-label="Move down"
        >
          ↓
        </button>
      </div>

      {editing ? (
        <div className="flex flex-col gap-2">
          <input
            className="auth-input"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <textarea
            className="auth-input"
            rows={2}
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {attachment.kind === "pdf" && (
            <select
              className="auth-input"
              value={script}
              onChange={(e) => setScript(e.target.value as DocumentScript)}
            >
              {SCRIPT_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          )}
          <FolderField value={groupKey} onChange={setGroupKey} />
        </div>
      ) : (
        <div className="min-w-0">
          <p className="font-grotesk text-[0.66rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)]">
            {summary.kindLabel}
            {summary.scriptLabel && (
              <>
                <span className="mx-2">·</span>
                {summary.scriptLabel}
              </>
            )}
            {summary.groupLabel && (
              <>
                <span className="mx-2">·</span>
                {summary.groupLabel}
              </>
            )}
          </p>
          <p className="font-display text-[1.15rem] leading-[1.25] tracking-tight">
            {attachment.label}
          </p>
          {attachment.description && (
            <p className="text-[0.9rem] leading-[1.5] text-[var(--color-ink-soft)] mt-1">
              {attachment.description}
            </p>
          )}
          <p className="font-grotesk text-[0.7rem] text-[var(--color-ink-mute)] mt-1.5">
            {summary.targetText}
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-baseline justify-end">
        {editing ? (
          <>
            <button
              type="button"
              className="row-action"
              onClick={() => {
                setEditing(false);
                setLabel(attachment.label);
                setDescription(attachment.description ?? "");
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="row-action"
              onClick={() => void onSave()}
              disabled={update.isPending}
            >
              {update.isPending ? "Saving…" : "Save"}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="row-action" onClick={() => setEditing(true)}>
              Edit
            </button>
            <button
              type="button"
              className="row-action is-danger"
              onClick={() => void onDelete()}
            >
              Delete
            </button>
          </>
        )}
      </div>
    </li>
  );
}

function attachmentSummary(a: PublicDocumentAttachment): {
  kindLabel: string;
  scriptLabel: string | null;
  groupLabel: string | null;
  targetText: string;
} {
  const kindLabel: Record<DocumentAttachmentKind, string> = {
    pdf: "PDF",
    audio: "Audio",
    video: "Video",
    youtube_video: "YouTube video",
    youtube_playlist: "YouTube playlist",
    external_link: "External link",
    file: "File",
  };
  const scriptLabel = a.script
    ? SCRIPT_OPTIONS.find((s) => s.value === a.script)?.label ?? null
    : null;
  const groupLabel = a.groupKey ? a.groupKey.split("/").join(" › ") : null;
  let targetText = "";
  if (a.kind === "pdf" || a.kind === "audio" || a.kind === "video") {
    targetText = a.fileUrl ?? "(missing file)";
  } else if (a.kind === "external_link") {
    targetText = a.externalUrl ?? "(missing url)";
  } else if (a.kind === "youtube_video") {
    targetText = a.youtubeVideo
      ? `${a.youtubeVideo.title} · ${a.youtubeVideoId}`
      : `${a.youtubeVideoId} (not synced yet)`;
  } else if (a.kind === "youtube_playlist") {
    targetText = a.youtubePlaylist
      ? `${a.youtubePlaylist.title} · ${a.youtubePlaylist.itemCount} items`
      : `${a.youtubePlaylistId} (not synced yet)`;
  }
  return { kindLabel: kindLabel[a.kind], scriptLabel, groupLabel, targetText };
}
